import { useState } from "react";
import { Reveal } from "./Reveal";

const included = [
  {
    title: "A support plan tailored to you",
    desc: "At the start, we decide together what works for your life: Zoom calls, voice notes, text, or a mix. Some moms love talking things through live, and others do best with a voice note between bedtimes. You choose.",
  },
  {
    title: "Text and voice-note support",
    desc: "Text or voice-note me whenever you want the right words in the moment: at the store, at bedtime, in the middle of a big feeling. I'll send back what to say, so calm and confident is what comes out.",
  },
  {
    title: "Your family phrase map",
    desc: "A personalized language map for your hardest moments — the store, bath time, bedtime.",
  },
  {
    title: "Teachings you won't find anywhere else",
    desc: "The 'Children should ___' exercise, the Language ACE framework, and age-by-age language for each scenario.",
  },
];

const expectations = [
  "I respond Monday through Friday, between 12:00 and 9:00 PM Eastern, within 24 business hours.",
  "Each day you can send up to two voice notes (up to 3 minutes each) or two text exchanges.",
  "Messages sent after 9 PM Friday, on weekends, or on holidays get answered the next business day.",
  "This is parent coaching, not therapy or speech therapy, and it isn't an emergency service. In a crisis, call 911 or 988.",
];

const bonus = {
  title: "The Abundance Language Audit ($197)",
  desc: "You'll get this the day you sign, before our first session. It shows you how we accidentally program scarcity into our kids through the words we say right after 'no,' so you walk into our work already hearing your own patterns.",
};

const StoryCard = ({ testid, readMoreTestId, title, children }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <figure
      data-testid={testid}
      className="rounded-2xl bg-white border border-[#EFE6D8] p-7 sm:p-10 shadow-[0_16px_40px_-24px_rgba(36,30,26,0.25)]"
    >
      <span className="font-serif-display text-5xl leading-none text-[#C69C6D]">&ldquo;</span>
      <h3 className="mt-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#8C4A32]">{title}</h3>
      <blockquote
        className={`mt-4 text-base sm:text-lg leading-relaxed text-[#5C524A] ${
          expanded ? "" : "line-clamp-4 sm:line-clamp-none"
        }`}
      >
        {children}
      </blockquote>
      {!expanded && (
        <button
          data-testid={readMoreTestId}
          onClick={() => setExpanded(true)}
          className="sm:hidden mt-3 text-sm font-semibold text-[#8C4A32] underline underline-offset-4"
        >
          Read more
        </button>
      )}
    </figure>
  );
};

export const Included = () => (
  <section data-testid="included-section" className="py-16 sm:py-24 lg:py-32">
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4">What's included</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-[#241E1A] font-medium">
              Six months. Private. Entirely around your family.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-7 space-y-4">
          {included.map((item, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`included-item-${i + 1}`} className="rounded-2xl bg-[#F4EFE6] border border-[#E6DEC3] p-6 sm:p-7 flex gap-5 items-start transition-transform duration-500 hover:-translate-y-1">
                <span className="mt-1 w-2 h-2 rounded-full bg-[#8C4A32] shrink-0" />
                <div>
                  <h3 className="font-serif-display text-xl sm:text-2xl text-[#241E1A] font-semibold">{item.title}</h3>
                  <p className="mt-1.5 text-base leading-relaxed text-[#5C524A]">{item.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal>
        <div
          data-testid="support-expectations"
          className="mt-14 sm:mt-16 rounded-2xl bg-[#F4EFE6] border border-[#E6DEC3] p-7 sm:p-10"
        >
          <h3 className="font-serif-display text-2xl sm:text-3xl text-[#241E1A] font-medium">
            What to expect from my support
          </h3>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#5C524A]">
            I keep my boundaries clear so every answer is focused and thoughtful.
          </p>
          <ul className="mt-6 space-y-3">
            {expectations.map((e, i) => (
              <li
                key={i}
                data-testid={`support-expectation-${i + 1}`}
                className="flex gap-3 items-start text-sm sm:text-base leading-relaxed text-[#5C524A]"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C69C6D] shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      <div data-testid="bonuses-section" className="mt-14 sm:mt-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4">Bonus</p>
          <h3 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#241E1A] font-medium">
            Your bonus (lifetime access)
          </h3>
        </Reveal>
        <Reveal delay={0.08}>
          <div
            data-testid="bonus-card-1"
            className="mt-8 max-w-2xl rounded-2xl bg-white border border-[#EFE6D8] p-6 sm:p-7 transition-transform duration-500 hover:-translate-y-1"
          >
            <h4 className="font-serif-display text-lg sm:text-xl text-[#241E1A] font-semibold leading-snug">
              {bonus.title}
            </h4>
            <p className="mt-2 text-sm sm:text-base leading-relaxed text-[#5C524A]">{bonus.desc}</p>
          </div>
        </Reveal>
      </div>

      <div className="mt-24 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-4">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#8C4A32] mb-4">What moms are saying</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl leading-[1.15] text-[#241E1A] font-medium">
              Real mothers. Real mornings after.
            </h2>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#5C524A]">
              Most moms come to me for the hard moments: the store, the whining, the meltdowns.
              That's where we start, because it's hard to build your child's inner voice while
              you're in survival mode. Once those moments get calmer, the deeper language work has
              room to land.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-8 space-y-6">
          <Reveal>
            <StoryCard testid="testimonial-card-allison" readMoreTestId="allison-story-read-more" title="Allison, mom of 3 boys">
              When Allison first came to me, she was doing curbside pickup to avoid taking her boys
              into the store. Too often, one of them would end up crying on the floor over a toy, and
              she'd be sitting there with him, getting stared down by strangers. It stirred up her
              own relationship with scarcity, even though she'd done the mindset work, read the
              books, and followed all the parenting pages. She still avoided the outing, and when her
              kids asked for something, she'd say, 'Ask your dad.'
              <br />
              <br />
              Then she prepped her 5-year-old before the store, the way we'd practiced. He picked
              everything up like usual. And she told me:{" "}
              <strong className="font-semibold text-[#241E1A]">
                'Not one single meltdown... Hours later I'm still in shock.'
              </strong>
            </StoryCard>
          </Reveal>
          <Reveal delay={0.12}>
            <StoryCard testid="testimonial-card-victoria" readMoreTestId="victoria-story-read-more" title="Victoria, mom of 2 boys">
              When Victoria first came to me, her son whined constantly through the day, mostly with
              her. 'Noooo mummy do it.' 'I cannnnt,' even though he could. It happened when he was
              frustrated and couldn't find his words, when she said no to something small like the
              wrong shoes or the wrong order of going down the stairs, and when his dad tried to help
              instead of her. And when she asked him to tell her what he wanted, it went from whining
              to shouting.
              <br />
              <br />
              She tensed up the second it started. She dreaded it, and she felt stuck between giving
              in and setting a pattern she didn't want. She rated herself a 10 out of 10 drained by
              the whining.
              <br />
              <br />
              Two weeks later, she rated herself a 2 out of 10.
              <br />
              <br />
              Her aha moment was realizing she didn't have to negotiate or over-explain. The whining
              decreased because she stopped giving in and started modeling how she wanted them to
              speak.
              <br />
              <br />
              She told me:{" "}
              <strong className="font-semibold text-[#241E1A]">
                'I feel like I'm in control. I no longer tense at the thought of my 3-year-old's
                reaction when I say something.'
              </strong>
            </StoryCard>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
